import React from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { AuthScreen } from "../features/auth/AuthScreen";
import { SupportAdvertiserList } from "../features/support/SupportAdvertiserList";
import { MainLayout } from "./MainLayout";
import { DashboardScreen } from "../features/dashboard/DashboardScreen";
import { CreativeScreen } from "../features/creative/CreativeScreen";
import { ReportsScreen } from "../features/reports/ReportsScreen";

// Wrapper that injects ga props into MainLayout so the nav knows to open integrations.
function MainLayoutWithGa({ gaOpenIntegrations, gaErrorFromUrl }) {
  return <MainLayout gaOpenIntegrations={gaOpenIntegrations} gaErrorFromUrl={gaErrorFromUrl} />;
}

// Removes a single query param from the URL without a history entry.
function useStripParam(name) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  return React.useCallback(() => {
    if (!searchParams.has(name)) return;
    const next = new URLSearchParams(searchParams);
    next.delete(name);
    const search = next.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : "" },
      { replace: true },
    );
  }, [navigate, location.pathname, searchParams, name]);
}

function LoadingShim() {
  return (
    <div style={{ minHeight:"100vh", background:"#FFFFFF", fontFamily:"'DM Sans','Helvetica Neue',sans-serif", color:"#151515", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <span style={{ color:"#626262" }}>Loading…</span>
    </div>
  );
}

function RequireAuth() {
  const { session, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!session) return <Navigate to="/login" replace state={{ from: location }} />;
  return <Outlet />;
}

function RequireSupport() {
  const { isSupportUser } = useAuth();
  if (!isSupportUser) return <Navigate to="/" replace />;
  return <Outlet />;
}

export function AuthGate() {
  const {
    session,
    loading,
    signOut,
    isSupportUser,
    masqueradeAdvertiserId,
    setMasquerade,
  } = useAuth();
  const [searchParams] = useSearchParams();
  const stripLogout = useStripParam("logout");
  const stripAdvId = useStripParam("advertiser_id");
  const stripGa = useStripParam("ga");

  // ?ga= one-shot: signals the integrations panel to open after OAuth callback.
  const [gaOpenIntegrations, setGaOpenIntegrations] = React.useState(false);
  const [gaErrorFromUrl, setGaErrorFromUrl] = React.useState(false);

  // 1. ?logout=1 one-shot — preserves prior behavior.
  React.useEffect(() => {
    if (searchParams.get("logout") === "1") {
      signOut();
      stripLogout();
    }
  }, [searchParams, signOut, stripLogout]);

  // 2. ?advertiser_id=N deep link — only meaningful for support users.
  // Guarded on !loading && session so the param stays in the URL until the
  // session resolves; we never silently no-op on a transient loading state.
  React.useEffect(() => {
    if (loading || !session) return;
    const raw = searchParams.get("advertiser_id");
    if (raw == null) return;
    if (!isSupportUser) {
      stripAdvId();
      return;
    }
    const trimmed = raw.trim();
    const n = parseInt(trimmed, 10);
    if (Number.isFinite(n) && String(n) === trimmed) {
      setMasquerade(n, "");
    }
    stripAdvId();
  }, [loading, session, isSupportUser, searchParams, setMasquerade, stripAdvId]);

  // 3. ?ga=connected / ?ga=error — one-shot post-OAuth redirect handling.
  // Strip the param immediately and signal the integrations panel.
  React.useEffect(() => {
    const gaParam = searchParams.get("ga");
    if (gaParam == null) return;
    if (gaParam === "connected") {
      setGaOpenIntegrations(true);
      setGaErrorFromUrl(false);
    } else if (gaParam === "error") {
      setGaErrorFromUrl(true);
      setGaOpenIntegrations(true);
    }
    stripGa();
  }, [searchParams, stripGa]);

  if (loading) return <LoadingShim />;

  // Hold the route tree while a ?advertiser_id deep link is present so
  // screens never mount with a stale effectiveAdvertiserId before the
  // masquerade effect above has a chance to run.
  if (session && searchParams.has("advertiser_id")) {
    return <LoadingShim />;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          session ? (
            <Navigate
              to={isSupportUser && masqueradeAdvertiserId == null ? "/support" : "/"}
              replace
            />
          ) : (
            <AuthScreen />
          )
        }
      />

      <Route element={<RequireAuth />}>
        {/* Support-only branch */}
        <Route element={<RequireSupport />}>
          <Route path="/support" element={<SupportAdvertiserList />} />
        </Route>

        {/* Main app branch — support users without an active masquerade are
            redirected to /support from the index route render below. */}
        <Route element={<MainLayoutWithGa gaOpenIntegrations={gaOpenIntegrations} gaErrorFromUrl={gaErrorFromUrl} />}>
          <Route
            index
            element={
              isSupportUser && masqueradeAdvertiserId == null
                ? <Navigate to="/support" replace />
                : <DashboardScreen />
            }
          />
          <Route path="creative" element={<CreativeScreen />} />
          <Route path="reports" element={<ReportsScreen />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
